/*
  DUNK-2: rotating announcement bar messages.

  Self-contained so theme.js (Symmetry) stays untouched: it initialises on page load and
  re-initialises on theme editor section reloads. The schedule check and the rotation state
  machine are DOM-free and exported for `node --test` (tests/announcement-bar.test.js).
*/
(function (root) {
  'use strict';

  // startSec / endSec are epoch seconds rendered by Liquid; 0 means no limit.
  function isInSchedule(startSec, endSec, nowMs) {
    var nowSec = nowMs / 1000;
    if (startSec > 0 && nowSec < startSec) return false;
    if (endSec > 0 && nowSec > endSec) return false;
    return true;
  }

  // Cycles through `count` messages, calling show(index) whenever the visible one changes.
  // Rotation stops while any pause reason is set ('hover', 'touch', 'focus', 'hidden', 'motion', 'editor').
  function createRotator(options) {
    var count = options.count;
    var intervalMs = options.intervalMs;
    var show = options.show;
    var index = 0;
    var timer = null;
    var reasons = {};

    function isRotating() {
      return count > 1 && intervalMs > 0;
    }

    function anyPaused() {
      return Object.keys(reasons).length > 0;
    }

    function restart() {
      clearInterval(timer);
      timer = null;
      if (isRotating() && !anyPaused()) {
        timer = setInterval(function () {
          index = (index + 1) % count;
          show(index);
        }, intervalMs);
      }
    }

    return {
      start: function () {
        show(index);
        restart();
      },
      select: function (i) {
        index = i;
        show(index);
        restart();
      },
      pause: function (reason) {
        reasons[reason] = true;
        restart();
      },
      resume: function (reason) {
        delete reasons[reason];
        restart();
      },
      isPaused: function (reason) {
        return reason ? reasons[reason] === true : anyPaused();
      },
      isRotating: isRotating,
      current: function () {
        return index;
      },
      destroy: function () {
        clearInterval(timer);
        timer = null;
      }
    };
  }

  if (typeof window === 'undefined' && typeof module === 'object' && module.exports) {
    module.exports = { isInSchedule: isInSchedule, createRotator: createRotator };
    return;
  }

  if (root.DunkertonsAnnouncementBar) return; // script included more than once

  var instances = [];

  function AnnouncementBar(el) {
    var self = this;
    var now = Date.now();
    var designMode = !!(root.Shopify && root.Shopify.designMode);

    this.el = el;
    this.list = el.querySelector('[data-announcement-messages]');
    this.all = Array.prototype.slice.call(el.querySelectorAll('[data-announcement-message]'));
    // Liquid already filtered by schedule; re-check in case the page was served from cache.
    this.messages = designMode ? this.all : this.all.filter(function (message) {
      return isInSchedule(Number(message.getAttribute('data-start')) || 0, Number(message.getAttribute('data-end')) || 0, now);
    });

    this.rotator = createRotator({
      count: this.messages.length,
      intervalMs: parseFloat(this.list.getAttribute('data-interval')) || 0,
      show: function (index) {
        self.show(self.messages[index]);
      }
    });

    this.onEnter = function (event) {
      if (event.pointerType === 'mouse') self.rotator.pause('hover');
    };
    this.onLeave = function () {
      self.rotator.resume('hover');
    };
    // Touch has no hover: a touch on the messages pauses them until the next touch elsewhere on the page.
    this.onTouch = function (event) {
      if (event.pointerType !== 'mouse') self.rotator.pause('touch');
    };
    this.onTouchElsewhere = function (event) {
      if (!self.list.contains(event.target)) self.rotator.resume('touch');
    };
    // Coming back from a tapped link restores the page from the back/forward cache still paused.
    this.onPageShow = function (event) {
      if (event.persisted) self.rotator.resume('touch');
    };
    this.onFocusIn = function () {
      self.rotator.pause('focus');
    };
    this.onFocusOut = function (event) {
      if (!self.list.contains(event.relatedTarget)) self.rotator.resume('focus');
    };
    this.onVisibility = function () {
      if (document.hidden) {
        self.rotator.pause('hidden');
      } else {
        self.rotator.resume('hidden');
      }
    };

    if (this.messages.length === 0) {
      this.el.style.display = 'none'; // every message expired since the page was cached
      return;
    }

    if (this.rotator.isRotating()) {
      if (root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.rotator.pause('motion');
      }
      this.list.addEventListener('pointerenter', this.onEnter);
      this.list.addEventListener('pointerleave', this.onLeave);
      this.list.addEventListener('pointerdown', this.onTouch);
      document.addEventListener('pointerdown', this.onTouchElsewhere);
      root.addEventListener('pageshow', this.onPageShow);
      this.list.addEventListener('focusin', this.onFocusIn);
      this.list.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onVisibility();
    }

    this.rotator.start();
  }

  AnnouncementBar.prototype.show = function (active) {
    this.all.forEach(function (message) {
      var isActive = message === active;
      message.classList.toggle('is-active', isActive);
      if (isActive) {
        message.removeAttribute('aria-hidden');
      } else {
        message.setAttribute('aria-hidden', 'true');
      }
    });
  };

  // Theme editor: show the selected message and hold it until it is deselected.
  AnnouncementBar.prototype.selectBlock = function (block) {
    var index = this.messages.indexOf(block);
    if (index === -1) return;
    this.rotator.pause('editor');
    this.rotator.select(index);
  };

  AnnouncementBar.prototype.deselectBlock = function () {
    this.rotator.resume('editor');
  };

  AnnouncementBar.prototype.destroy = function () {
    this.rotator.destroy();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('pointerdown', this.onTouchElsewhere);
    root.removeEventListener('pageshow', this.onPageShow);
  };

  function init(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-announcement-bar]'), function (el) {
      if (el.hasAttribute('data-announcement-init')) return;
      el.setAttribute('data-announcement-init', '');
      instances.push(new AnnouncementBar(el));
    });
  }

  function destroyWithin(scope) {
    instances = instances.filter(function (instance) {
      if (!scope.contains(instance.el)) return true;
      instance.destroy();
      return false;
    });
  }

  function instanceFor(target) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].el.contains(target)) return instances[i];
    }
    return null;
  }

  root.DunkertonsAnnouncementBar = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var instance = instanceFor(event.target);
    if (instance) instance.selectBlock(event.target);
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var instance = instanceFor(event.target);
    if (instance) instance.deselectBlock();
  });
})(typeof window !== 'undefined' ? window : this);
